import { useEffect, useRef, useState } from "react";

// =============================================================================
// useCountUp — animate a number from its previous value to a new target.
// =============================================================================
// The JS half of the ANIMATION TOOLKIT (see /animate skill). CSS keyframes
// can't tween rendered TEXT, so number count-ups need a rAF loop. Pair with
// <CountUp> (components/ui/CountUp.tsx), which runs the caller's formatter
// over the animated value every frame — the final frame lands EXACTLY on the
// target, so the settled string is byte-identical to a static render.
//
// `start: false` holds the tween at its origin (0 on mount) — <CountUp> uses
// this to wait until the element scrolls into view. Target changes while held
// just retarget the pending tween.
//
// prefers-reduced-motion → return the target untweened, ignoring `start`
// (held numbers must still read correctly off-screen). Playwright runs with
// reducedMotion:"reduce" (playwright.config.ts) so specs that read number
// text one-shot — even below the fold — always see settled values.
// =============================================================================

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export interface CountUpOptions {
  /** Animation length in ms. Default 900 — matches the .anim-fill bar wipe. */
  duration?: number;
  /** Delay before the tween starts (ms) — for staggering alongside .anim-fill bars. */
  delay?: number;
  /** Hold the tween until true (e.g. element scrolled into view). Default true. */
  start?: boolean;
}

export function useCountUp(target: number, opts?: CountUpOptions): number {
  const duration = opts?.duration ?? 900;
  const delay = opts?.delay ?? 0;
  const start = opts?.start ?? true;

  const [display, setDisplay] = useState(0);
  // Tween origin: tracks the currently-displayed value so a target change
  // (cache → fresh RPC merge, window flip) animates FROM what's on screen,
  // not from 0.
  const fromRef = useRef(0);

  useEffect(() => {
    if (!Number.isFinite(target) || prefersReducedMotion()) {
      fromRef.current = Number.isFinite(target) ? target : 0;
      return;
    }
    if (!start) return; // held — tween fires when `start` flips true
    const from = fromRef.current;
    if (from === target) return;

    let raf = 0;
    let begin: number | null = null;
    const tick = (now: number) => {
      if (begin === null) begin = now + delay;
      const elapsed = now - begin;
      if (elapsed < 0) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const p = Math.min(elapsed / duration, 1);
      // p=1 → value === target exactly; formatters render the true final string
      const value = from + (target - from) * easeOutCubic(p);
      fromRef.current = value;
      setDisplay(value);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, delay, start]);

  // Snap path (reduced motion / non-finite) bypasses state entirely; the
  // animated path renders whatever the rAF loop last committed.
  if (!Number.isFinite(target) || prefersReducedMotion()) return target;
  return display;
}
